import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotes } from '../context/NotesContext';
import { useTest } from '../context/TestContext';
import { useError } from '../context/ErrorContext';
import Sidebar from '../components/Sidebar';
import HamburgerButton from '../components/HamburgerButton';
import { apiService } from '../services/apiService';
import { CogIcon, DocumentTextIcon, DocumentIcon, CheckCircleIcon, SparklesIcon, MagnifyingGlassIcon, XMarkIcon, FolderIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import TestLoadingOverlay from '../components/TestLoadingOverlay';

const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];
const QUESTION_TYPES = ['multiple-choice', 'true-false', 'short-answer'];

const TestCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notes } = useNotes();
  const { createTest } = useTest();
  const { showError } = useError();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Test configuration states
  const [testTitle, setTestTitle] = useState('');
  const [questionCount, setQuestionCount] = useState(5);
  const [difficulty, setDifficulty] = useState('Medium');
  const [timeLimit, setTimeLimit] = useState(0); // 0 means no time limit
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState(['multiple-choice']);
  const MAX_QUESTIONS = 30;

  // Add this with your other state variables
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('All Folders');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add this near the top with your other state variables
  const loadingMessages = [
    "Hacking university database to steal next semester's questions...",
    "Bribing the AI with virtual cookies...",
    "Teaching AI how to write impossible questions...",
    "Convincing ChatGPT to be less friendly...",
    "Calculating the perfect amount of trick questions...",
    "Searching Stack Overflow for the hardest questions...",
    "Mixing easy and impossible questions for maximum chaos...",
    "Consulting with evil professors for inspiration...",
    "Adding extra difficult options just for fun...",
    "Making sure the answers are more confusing than the questions...",
    "Generating questions that even the AI can't answer...",
    "Adding a sprinkle of 'Wait, we didn't cover this!'...",
  ];

  // Add this state
  const [currentLoadingMessage, setCurrentLoadingMessage] = useState(0);

  // Update the folders array to completely exclude 'Default' and empty folders
  const folders = ['All Folders', ...new Set(notes
    .map(note => note.folder)
    .filter(folder => folder && folder !== 'Default'))]; // Only keep non-null/undefined and non-'Default' folders

  // Update the filter function to handle notes without folders or with 'Default' folder
  const filteredNotes = notes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = selectedFolder === 'All Folders' || 
      (note.folder === selectedFolder && note.folder !== 'Default');
    return matchesSearch && matchesFolder;
  });

  const handleNoteSelect = (note) => {
    setSelectedNotes(prev => {
      const isSelected = prev.some(n => n._id === note._id);
      if (isSelected) {
        return prev.filter(n => n._id !== note._id);
      } else {
        return [...prev, note];
      }
    });
  };

  const handleGenerateTest = async () => {
    if (selectedNotes.length === 0) {
      showError('Please select at least one note');
      return;
    }

    setIsGenerating(true);
    try {
      const userId = JSON.parse(localStorage.getItem('user'))?.userId;
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Log the request payload
      console.log("Sending test generation request:", {
        notes: selectedNotes,
        title: testTitle,
        userId,
        settings: {
          questionCount: questionCount,
          difficulty,
          timeLimit,
          questionTypes: selectedQuestionTypes
        }
      });

      const response = await apiService.post('/api/tests/generate', {
        notes: selectedNotes,
        title: testTitle,
        userId,
        settings: {
          questionCount: questionCount,
          difficulty,
          timeLimit,
          questionTypes: selectedQuestionTypes
        }
      });

      if (!response.questions || response.questions.length === 0) {
        throw new Error('No questions were generated');
      }

      navigate('/test/take', { state: { test: response } });
    } catch (error) {
      console.error('Test generation error:', error);
      showError(error.message || 'Failed to generate test');
    } finally {
      setIsGenerating(false);
    }
  };

  // Question Count input handler
  const handleQuestionCountChange = (e) => {
    const input = e.target.value;
    
    // Allow empty input
    if (input === '') {
      setQuestionCount('');
      return;
    }

    // Parse number and apply limits
    const value = parseInt(input);
    if (!isNaN(value)) {
      setQuestionCount(Math.min(Math.max(1, value), MAX_QUESTIONS));
    }
  };

  // Add this handler with your other handlers
  const handleTimeLimitChange = (e) => {
    const input = e.target.value;
    
    // Allow empty input
    if (input === '') {
      setTimeLimit('');
      return;
    }

    // Parse number and ensure non-negative
    const value = parseInt(input);
    if (!isNaN(value)) {
      setTimeLimit(Math.max(0, value));
    }
  };

  // Add this effect when isGenerating is true
  useEffect(() => {
    let messageInterval;
    if (isGenerating) {
      messageInterval = setInterval(() => {
        setCurrentLoadingMessage(current => 
          current === loadingMessages.length - 1 ? 0 : current + 1
        );
      }, 3000); // Change message every 3 seconds
    }
    return () => clearInterval(messageInterval);
  }, [isGenerating]);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  // Add useEffect to close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Add useEffect to close sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Sidebar 
        isSidebarOpen={isSidebarOpen} 
        toggleSidebar={toggleSidebar}
      />
      <HamburgerButton 
        onClick={toggleSidebar}
        isOpen={isSidebarOpen}
      />

      <TestLoadingOverlay isVisible={isGenerating} />

      <main className={`transition-all duration-500 ease-in-out ${
        'ml-0 lg:ml-64'
      }`}>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-white mb-8">Create Test</h1>

            {/* Test Configuration */}
            <div className="bg-dark-card rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium text-white">Test Configuration</h2>
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <CogIcon className="w-5 h-5 text-blue-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {/* Test Title */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-gray-400 mb-2 text-sm">Test Title</label>
                  <input
                    type="text"
                    value={testTitle}
                    onChange={(e) => setTestTitle(e.target.value)}
                    placeholder="Enter test title"
                    className="w-full bg-dark-bg text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                </div>

                {/* Question Count */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-gray-400 mb-2 text-sm">Number of Questions</label>
                  <input
                    type="number"
                    value={questionCount}
                    onChange={handleQuestionCountChange}
                    onBlur={() => {
                      // When input loses focus, ensure there's at least 1
                      if (questionCount === '' || questionCount < 1) {
                        setQuestionCount(1);
                      }
                    }}
                    min="1"
                    max={MAX_QUESTIONS}
                    className="w-full bg-dark-bg text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum {MAX_QUESTIONS} questions allowed</p>
                </div>

                {/* Difficulty */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-gray-400 mb-2 text-sm">Difficulty</label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full bg-dark-bg text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors appearance-none"
                  >
                    {DIFFICULTY_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                {/* Time Limit */}
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-gray-400 mb-2 text-sm">Time Limit (minutes)</label>
                  <input
                    type="number"
                    value={timeLimit}
                    onChange={handleTimeLimitChange}
                    onBlur={() => {
                      // When input loses focus, ensure non-negative
                      if (timeLimit === '' || timeLimit < 0) {
                        setTimeLimit(0);
                      }
                    }}
                    min="0"
                    placeholder="0 for no limit"
                    className="w-full bg-dark-bg text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave at 0 for no time limit</p>
                </div>

                {/* Question Types */}
                <div className="col-span-2">
                  <label className="block text-gray-400 mb-3 text-sm">Question Types</label>
                  <div className="flex flex-wrap gap-4">
                    {QUESTION_TYPES.map(type => (
                      <label 
                        key={type} 
                        className={`flex items-center px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 ${
                          selectedQuestionTypes.includes(type)
                            ? 'bg-blue-500/10 border-blue-500 text-blue-500'
                            : 'border-gray-700 text-gray-400 hover:border-gray-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedQuestionTypes.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedQuestionTypes(prev => [...prev, type]);
                            } else if (selectedQuestionTypes.length > 1) {
                              setSelectedQuestionTypes(prev => prev.filter(t => t !== type));
                            }
                          }}
                          className="hidden"
                        />
                        <div className={`w-4 h-4 rounded border mr-2 flex items-center justify-center transition-colors ${
                          selectedQuestionTypes.includes(type)
                            ? 'bg-blue-500 border-blue-500'
                            : 'border-gray-600'
                        }`}>
                          {selectedQuestionTypes.includes(type) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="capitalize">{type.replace('-', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Notes Section */}
            <div className="bg-dark-card rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium text-white">Selected Notes</h2>
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <DocumentTextIcon className="w-5 h-5 text-purple-500" />
                </div>
              </div>

              {/* Search and Filter Controls */}
              <div className="mb-4 flex gap-3">
                {/* Search Input */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search notes..."
                    className="w-full bg-dark-bg text-white pl-10 pr-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                  <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Folder Filter */}
                <div className="relative min-w-[160px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                    className="w-full bg-dark-bg text-white pl-10 pr-4 py-2.5 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors flex items-center justify-between"
                  >
                    <span className="truncate">{selectedFolder}</span>
                    <ChevronDownIcon className="w-5 h-5 text-gray-400 ml-2" />
                  </button>

                  {isDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setIsDropdownOpen(false)}
                      ></div>
                      <div className="absolute mt-2 w-full z-20 bg-dark-card border border-gray-700 rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
                        {folders.map(folder => (
                          <button
                            key={folder}
                            onClick={() => {
                              setSelectedFolder(folder);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-left hover:bg-dark-hover transition-colors ${
                              selectedFolder === folder 
                                ? 'text-blue-500 bg-blue-500/10' 
                                : 'text-gray-300'
                            }`}
                          >
                            <div className="flex items-center">
                              <FolderIcon className={`w-4 h-4 mr-2 ${
                                selectedFolder === folder 
                                  ? 'text-blue-500' 
                                  : 'text-gray-400'
                              }`} />
                              <span className="truncate">{folder}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Scrollable Notes Container */}
              <div className="max-h-[400px] overflow-y-auto mb-4 pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-dark-bg">
                {notes.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="bg-dark-bg rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <DocumentTextIcon className="w-8 h-8 text-gray-500" />
                    </div>
                    <p className="text-gray-400">No notes available</p>
                  </div>
                ) : filteredNotes.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No notes found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredNotes.map(note => (
                      <div
                        key={note._id}
                        onClick={() => handleNoteSelect(note)}
                        className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                          selectedNotes.some(n => n._id === note._id)
                            ? 'bg-blue-500/10 border border-blue-500/50'
                            : 'bg-dark-bg border border-transparent hover:bg-dark-hover/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <DocumentIcon className={`w-5 h-5 ${
                              selectedNotes.some(n => n._id === note._id)
                                ? 'text-blue-500'
                                : 'text-gray-400'
                            }`} />
                            <div>
                              <h3 className={`font-medium ${
                                selectedNotes.some(n => n._id === note._id)
                                  ? 'text-blue-500'
                                  : 'text-white'
                              }`}>
                                {note.title || 'Untitled Note'}
                              </h3>
                              {note.folder && (
                                <span className="text-xs text-gray-400">
                                  {note.folder}
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedNotes.some(n => n._id === note._id) && (
                            <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                          )}
                        </div>
                        <p className="text-sm text-gray-400 line-clamp-1 pl-8">
                          {note.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Notes Count */}
              <div className="text-sm text-gray-400 mb-4">
                {selectedNotes.length} notes selected
              </div>
            </div>

            {/* Generate Button - Now outside the scrollable area */}
            <button
              onClick={handleGenerateTest}
              disabled={selectedNotes.length === 0 || isGenerating}
              className={`w-full p-4 rounded-xl font-medium transition-all duration-200 flex items-center justify-center space-x-2 ${
                selectedNotes.length === 0 || isGenerating
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <SparklesIcon className="w-5 h-5" />
              <span>Generate Test</span>
            </button>
          </div>
        </div>
      </main>

      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default TestCreate; 